import { EPricing_FacilityLookupCode } from './quickprice';

export type SeminarRoomFacilityLike = {
  lookupCode?: string | null;
  meetingRoom?: unknown | null;
};

export function isSeminarRoomFacilityLookupCode(lookupCode: string | null | undefined): boolean {
  return (
    lookupCode === EPricing_FacilityLookupCode.SEMINARROOM_FULLDAY ||
    lookupCode === EPricing_FacilityLookupCode.SEMINARROOM_SEMIDAY
  );
}

export function isSeminarRoomFacility(facility: SeminarRoomFacilityLike | null | undefined): boolean {
  if (!facility) return false;
  return isSeminarRoomFacilityLookupCode(facility.lookupCode) || !!facility.meetingRoom;
}

export function getSeminarRoomDisplayHeader(language: string | null | undefined): string {
  return (language || '').toUpperCase().startsWith('EN') ? 'Seminar Room' : 'Seminarraum';
}

export type SeminarRoomEquipment = {
  beamerCount?: number | null;
  flipchartCount?: number | null;
  pinboardCount?: number | null;
  requireAirConditioning?: boolean | null;
  requireDaylight?: boolean | null;
  /** Hotel-entered guaranteed size in m². 0 and empty mean not set. */
  guaranteedRoomAreaSqm?: number | null;
};

const equipmentLabels = {
  de: {
    prefix: 'Inkludiert',
    beamer: 'Beamer & Leinwand / Flatscreen',
    flipchart: 'Flipchart',
    pinboard: 'Pinnwand',
    air: 'Klimaanlage',
    daylight: 'Tageslicht',
    guaranteedSize: (size: number) => `${size}m² garantiert`,
  },
  en: {
    prefix: 'Included',
    beamer: 'Projector & Screen / Flatscreen',
    flipchart: 'Flipchart',
    pinboard: 'Pinboard',
    air: 'Air conditioning',
    daylight: 'Daylight',
    guaranteedSize: (size: number) => `${size}m² guaranteed`,
  },
};

/**
 * Equipment sentence for an Epilo seminar room.
 * A missing technology count uses defaultCount (1 for a booker request). 0 is omitted.
 * Air conditioning and daylight are included only when selected.
 * A guaranteed room size above 0 is appended.
 */
export function formatSeminarRoomEquipmentSentence(
  equipment: SeminarRoomEquipment | null | undefined,
  language: string | null | undefined,
  options?: { defaultCount?: number | null },
): string {
  const labels = (language || '').toUpperCase().startsWith('EN') ? equipmentLabels.en : equipmentLabels.de;
  const defaultCount = options?.defaultCount === undefined ? 1 : options.defaultCount;
  const count = (value?: number | null) => {
    const resolved = value == null ? defaultCount : value;
    if (resolved == null || resolved <= 0) return null;
    return resolved;
  };
  const parts: string[] = [];
  const beamer = count(equipment?.beamerCount);
  const flipchart = count(equipment?.flipchartCount);
  const pinboard = count(equipment?.pinboardCount);
  if (beamer) parts.push(`${beamer}x ${labels.beamer}`);
  if (flipchart) parts.push(`${flipchart}x ${labels.flipchart}`);
  if (pinboard) parts.push(`${pinboard}x ${labels.pinboard}`);
  if (equipment?.requireAirConditioning) parts.push(labels.air);
  if (equipment?.requireDaylight) parts.push(labels.daylight);
  if (equipment?.guaranteedRoomAreaSqm && equipment.guaranteedRoomAreaSqm > 0) {
    parts.push(labels.guaranteedSize(equipment.guaranteedRoomAreaSqm));
  }
  if (!parts.length) return '';
  return `${labels.prefix}: ${parts.join(', ')}`;
}

/** Replaces a previous equipment sentence so regenerated details do not repeat it. */
export function replaceSeminarRoomEquipmentSentence(details: string | null | undefined, sentence: string): string {
  const stripped = (details || '').replace(/(?:,\s*)?(?:Inkludiert|Included):[\s\S]*$/, '').trim().replace(/,\s*$/, '');
  if (!sentence) return stripped;
  return stripped ? `${stripped}, ${sentence}` : sentence;
}

/**
 * Copies booker equipment onto seminar-room line items, in occupancy order for that day.
 */
export function assignSeminarRoomEquipment<T extends { day: number; facility?: SeminarRoomFacilityLike | null; seminarRoomEquipment?: SeminarRoomEquipment | null }>(
  lineItems: T[],
  days: { day: number; occupancy?: SeminarRoomEquipment[] | null }[] | null | undefined,
) {
  const seen = new Map<number, number>();
  for (const lineItem of lineItems) {
    if (!isSeminarRoomFacility(lineItem.facility)) continue;
    const index = seen.get(lineItem.day) || 0;
    seen.set(lineItem.day, index + 1);
    const occupancy = days?.find(day => day.day === lineItem.day)?.occupancy?.[index];
    if (!occupancy) continue;
    lineItem.seminarRoomEquipment = {
      beamerCount: occupancy.beamerCount,
      flipchartCount: occupancy.flipchartCount,
      pinboardCount: occupancy.pinboardCount,
      requireAirConditioning: occupancy.requireAirConditioning,
      requireDaylight: occupancy.requireDaylight,
    };
  }
}
